/**
 * ChartConfigBuilder - Chart Configuration Service
 * 
 * SOLID Principle: Single Responsibility Principle (SRP)
 * Purpose: This service is solely responsible for building chart configuration
 * objects including axis setup, color assignment, and domain calculations.
 * It separates configuration logic from data processing and rendering concerns.
 * 
 * Responsibilities:
 * - Generate multi-axis configurations for different field types
 * - Calculate appropriate Y-axis domains based on data ranges
 * - Assign colors consistently across chart elements
 * - Handle axis positioning and scaling
 * 
 * Context: Buoy dashboard supports displaying multiple sensor types simultaneously,
 * each with different units and scales. Multi-axis setup allows comparing
 * temperature (°C) with pressure (dbar) with CO2 (μatm) on the same timeline.
 */

import { FieldTypeRegistry } from './FieldTypeRegistry.js';
import { LabelUtils } from './LabelUtils.js';
import { ColorUtils } from './ColorUtils.js';

// Color palette moved to ColorUtils.js to eliminate duplication

export class ChartConfigBuilder {
  /**
   * Builds complete axis configuration for multi-axis chart display
   * 
   * Creates one Y-axis per regular (non-directional) field, alternating between
   * left and right sides. Each axis gets its own color, units, and scaling.
   * 
   * @param {Array} regularFields - Non-directional field keys to display
   * @param {Array} chartData - Processed chart data for domain calculation
   * @param {boolean} hasDirectionalFields - Whether directional arrows need space
   * @returns {Array} Array of axis configuration objects
   */
  static buildAxisConfiguration(regularFields, chartData = [], hasDirectionalFields = false) {
    return regularFields.map((fieldKey, index) => {
      // Alternate axes between left and right sides for better readability
      const orientation = index % 2 === 0 ? 'left' : 'right';
      const yAxisId = `yAxis-${index}`;
      
      // Use consolidated color utility
      const color = ColorUtils.getFieldColor(fieldKey, index);
      
      // Get field-specific configuration
      const units = FieldTypeRegistry.getUnits(fieldKey);
      const fieldType = FieldTypeRegistry.getFieldType(fieldKey);
      const label = LabelUtils.getAxisLabel(fieldKey);
      
      // Calculate appropriate data range for this field
      const domain = this._calculateDomain(fieldKey, chartData, hasDirectionalFields);
      
      return {
        fieldKey,
        yAxisId,
        orientation,
        color,
        units,
        label,
        domain,
        fieldType,
        index
      };
    });
  }

  /**
   * Calculates appropriate Y-axis domain (min/max) for a specific field
   * 
   * Analyzes data values and adds padding for better visualization.
   * Handles edge cases like single values, invalid data, and extreme ranges.
   * Extends domain below zero to accommodate directional arrows.
   * 
   * @param {string} fieldKey - Field identifier
   * @param {Array} chartData - Chart data array
   * @param {boolean} hasDirectionalFields - Whether directional arrows need space
   * @returns {Array} [min, max] domain values
   * @private
   */
  static _calculateDomain(fieldKey, chartData, hasDirectionalFields = false) {
    // Extract valid values for this field
    const values = chartData
      .map(item => item[fieldKey])
      .filter(val => typeof val === 'number' && !isNaN(val) && isFinite(val));
    
    // Return auto-scaling if no valid data
    if (values.length === 0) {
      return ['auto', 'auto'];
    }
    
    const min = Math.min(...values);
    const max = Math.max(...values);
    
    // Handle single-value case
    if (min === max) {
      const padding = Math.abs(max) * 0.1 || 1;
      const calculatedMin = min - padding;
      const calculatedMax = max + padding;
      
      // Extend domain below zero for directional arrows
      return hasDirectionalFields 
        ? [Math.min(calculatedMin, -0.2), calculatedMax]
        : [calculatedMin, calculatedMax];
    }
    
    // Add 5% padding to range for better visualization
    const range = max - min;
    const padding = range * 0.05;
    const calculatedMin = min - padding;
    
    // Extend domain below zero for directional arrows
    return hasDirectionalFields 
      ? [Math.min(calculatedMin, -0.2), max + padding]
      : [calculatedMin, max + padding];
  }

  // _getFieldLabel method removed - using LabelUtils.getFieldLabel instead

  /**
   * Calculates axis offset for proper spacing when multiple axes share same orientation
   * 
   * When multiple fields use the same side (left/right), they need to be spaced
   * apart so axis labels don't overlap.
   * 
   * @param {number} axisIndex - Index of current axis
   * @param {string} orientation - 'left' or 'right'
   * @param {Array} allAxes - All axis configurations
   * @returns {number} Offset in pixels
   */
  static calculateAxisOffset(axisIndex, orientation, allAxes) {
    // Count how many axes of same orientation come before this one
    const sameOrientationAxes = allAxes
      .slice(0, axisIndex)
      .filter(axis => axis.orientation === orientation);
    
    // 80px spacing between axes of same orientation
    return sameOrientationAxes.length * 80;
  }

  /**
   * Builds color mapping for consistent coloring across chart elements
   * 
   * Delegates to ColorUtils to eliminate duplication
   * 
   * @param {Array} fields - Field keys to assign colors to
   * @returns {Object} Map of fieldKey -> color
   */
  static buildColorMapping(fields) {
    return ColorUtils.buildColorMapping(fields);
  }

  /**
   * Generates tick formatter function for Y-axis
   * 
   * Creates appropriate number formatting based on field type and data range.
   * Includes units in tick labels for clarity.
   * 
   * @param {Object} axisConfig - Axis configuration object
   * @returns {Function} Tick formatter function
   */
  static createTickFormatter(axisConfig) {
    const { units, fieldType } = axisConfig;
    
    return (value) => {
      if (value === null || value === undefined || isNaN(value)) {
        return '';
      }
      
      // Use field-specific precision (safety check for undefined fieldType)
      const precision = fieldType?.precision || 1;
      return `${value.toFixed(precision)} ${units}`;
    };
  }

  /**
   * Builds configuration for chart brush/zoom component
   * 
   * @param {Array} chartData - Chart data for range calculation
   * @returns {Object} Brush configuration
   */
  static buildBrushConfig(chartData) {
    if (!chartData.length) {
      return null;
    }
    
    const dataLength = chartData.length;
    
    // Show last 20% of data by default, or minimum 10 points
    const defaultStartIndex = Math.max(0, dataLength - Math.max(Math.floor(dataLength * 0.2), 10));
    
    return {
      dataKey: 'time',
      height: 30,
      startIndex: defaultStartIndex,
      endIndex: dataLength - 1
    };
  }

  /**
   * Generates chart margin configuration based on number of axes
   * 
   * Adjusts margins to accommodate multiple Y-axes and their labels.
   * 
   * @param {Array} axisConfigs - All axis configurations
   * @param {boolean} hasDirectionalFields - Whether directional arrows are shown
   * @returns {Object} Margin configuration
   */
  static calculateChartMargins(axisConfigs, hasDirectionalFields = false) {
    const leftAxes = axisConfigs.filter(axis => axis.orientation === 'left').length;
    const rightAxes = axisConfigs.filter(axis => axis.orientation === 'right').length;
    
    return {
      left: 20,
      right: 20,
      top: 10,
      bottom: 100 // More space between X-axis and time labels
    };
  }
}

export default ChartConfigBuilder;